import React, { useEffect, useState } from "react";
import Logo from "../components/images/logo.png";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveAnchor, scrollToAnchor } from "../utils/anchors";

// Each item can be:
//  - { type: "anchor", anchor: "#about", label: "About" }   // scroll on home, navigate to /#about elsewhere
//  - { type: "route", to: "/services", label: "Services" }   // hard route
const navItems = [
  { type: "anchor", anchor: "#top", label: "Home" },
  { type: "anchor", anchor: "#about", label: "About" },
  { type: "anchor", anchor: "#portfolio", label: "Portfolio" },
  { type: "anchor", anchor: "#services", label: "Services" },
  { type: "anchor", anchor: "#reviews", label: "Reviews" },
  { type: "anchor", anchor: "#contact", label: "Contact" },
];

const Navbar = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState("#top");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section the user has scrolled past (only on home).
  // Scroll-position based for reliability across route transitions / deep-links.
  useEffect(() => {
    if (pathname !== "/") return;

    const ids = ["top", "about", "portfolio", "services", "reviews", "contact"];
    let raf = null;
    let cancelled = false;

    const compute = () => {
      const probe = window.scrollY + window.innerHeight * 0.35; // line ~35% down the viewport
      let active = "#top";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= probe) {
          active = `#${id}`;
        }
      }
      setActiveAnchor(active);
    };

    const onScroll = () => {
      if (raf || cancelled) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        compute();
      });
    };

    // Wait for sections to mount, then run an initial compute
    let attempts = 0;
    const maxAttempts = 40;
    const initial = () => {
      if (cancelled) return;
      const ready = ids
        .map((id) => document.getElementById(id))
        .filter(Boolean).length;
      if (ready >= 4 || attempts >= maxAttempts) {
        compute();
      } else {
        attempts++;
        setTimeout(initial, 100);
      }
    };
    initial();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelled = true;
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  // Arrive at home with a hash (e.g. /#contact) → wait for the element to
  // exist (route transition / mount), scroll to it, then strip the hash.
  useEffect(() => {
    if (pathname !== "/" || !hash) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // up to ~4s

    const stripHash = () => window.history.replaceState(null, "", "/");

    const tryScroll = () => {
      if (cancelled) return;
      if (hash === "#top") {
        scrollToAnchor("#top");
        stripHash();
        return;
      }
      const { target } = resolveAnchor(hash);
      const el = document.querySelector(target);
      if (el && el.offsetTop > 50 && document.body.scrollHeight > window.innerHeight + 200) {
        scrollToAnchor(hash);
        stripHash();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, 100);
      } else {
        stripHash();
      }
    };

    // Wait for the route transition (AnimatePresence wait-mode ~450ms) before first attempt
    const t = setTimeout(tryScroll, 550);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pathname, hash]);

  const handleNavClick = (item, e) => {
    if (item.type === "anchor") {
      e.preventDefault();
      if (pathname === "/") scrollToAnchor(item.anchor);
      else navigate(item.anchor === "#top" ? "/" : `/${item.anchor}`);
      setOpen(false);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "py-2" : "py-3 md:py-4"
        }`}
      >
        <div
          className={`mx-auto max-w-6xl px-3 transition-all duration-500 ${
            scrolled ? "scale-[0.98]" : "scale-100"
          }`}
        >
          <div
            className={`flex items-center justify-between gap-4 rounded-full pl-2 pr-2 md:pl-3 md:pr-3 py-1.5 transition-all duration-500 ${
              scrolled
                ? "glass-strong shadow-card border border-white/10"
                : "bg-white/[0.03] border border-white/5 backdrop-blur-md"
            }`}
          >
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                if (pathname === "/") scrollToAnchor("#top");
                else navigate("/");
              }}
              className="flex items-center pl-2"
            >
              <img src={Logo} alt="Hamza" className="h-8 md:h-10" />
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const active = pathname === "/" && activeAnchor === item.anchor;
                return (
                  <a
                    key={item.anchor}
                    href={`/${item.anchor}`}
                    onClick={(e) => handleNavClick(item, e)}
                    className="relative px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            "linear-gradient(120deg, rgba(45,212,191,0.22), rgba(236,72,153,0.18))",
                          border: "1px solid rgba(45,212,191,0.25)",
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 380,
                          damping: 30,
                        }}
                      />
                    )}
                    <span
                      className={`relative ${
                        active ? "text-white font-medium" : ""
                      }`}
                    >
                      {item.label}
                    </span>
                  </a>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center">
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                  setOpen(false);
                }}
                className="btn-primary !py-2 !px-5 text-xs"
              >
                Let's talk
              </a>
            </div>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 mr-1 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white"
              aria-label="Menu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                {open ? (
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                ) : (
                  <path
                    d="M4 7h16M4 12h16M4 17h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 z-40 bg-ink-950/80 backdrop-blur-lg"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="pt-24 px-6"
              onClick={(e) => e.stopPropagation()}
            >
              <ul className="flex flex-col gap-2">
                {navItems.map((item, i) => {
                  const active =
                    pathname === "/" && activeAnchor === item.anchor;
                  return (
                    <motion.li
                      key={item.anchor}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.04 }}
                    >
                      <a
                        href={`/${item.anchor}`}
                        onClick={(e) => handleNavClick(item, e)}
                        className={`flex items-center justify-between px-5 py-4 rounded-2xl border ${
                          active
                            ? "border-aurora-teal/40 bg-white/[0.05] text-white"
                            : "border-white/10 bg-white/[0.02] text-white/70"
                        }`}
                      >
                        <span className="text-base font-medium">
                          {item.label}
                        </span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    </motion.li>
                  );
                })}
                <motion.li
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-3"
                >
                  <a
                    href="/contact"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(false);
                      navigate("/contact");
                    }}
                    className="btn-primary w-full justify-center"
                  >
                    Let's talk
                  </a>
                </motion.li>
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
